import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { FaChevronLeft } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Grid } from '@mui/material'

import { useGET_meetingByIdQuery, useGET_meetingProtocolByIdQuery, usePOST_meetingProtocolMutation } from 'redux/api/meetingsApi'

import { Form, Formik } from 'formik'

import SubmitButton from 'components/atoms/buttons/SubmitButton'
import DateTimePickerField from 'components/atoms/fields/DateTimePickerField'
import InputField from 'components/atoms/fields/InputField'
import RichTextField from 'components/atoms/fields/RichTextField'
import ParticipantList from 'components/utilized/meetings/ParticipantList'

import formModel, { initialValues, validationSchema } from 'utils/formModels/meetingProtocolModel'
import { crudColorMap, crudIconMap } from 'utils/helpers'

import { ActionType, IParticipant } from 'types'
import { useErrorAlert } from 'utils/customHooks/useErrorAlert'
import { useSuccessAlert } from 'utils/customHooks/useSuccessAlert'
import { ScrollToError } from 'components/atoms/ScrollToError'
import PageHeading from 'components/atoms/PageHeading'
import { CheckBoxOutlined } from '@mui/icons-material'

interface IProps {
  action: ActionType
  id: string
  viewOnly: boolean
  tabText?: string
  tabKey?: string
}

const MeetingProtocolForm: FC<IProps> = ({ action, id, viewOnly }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const backToSlug = '/meetings'

  const [addEntity, { isLoading, isSuccess: createSuccess, error: createError, data: addResponseData }] =
    usePOST_meetingProtocolMutation()

  const { data: entity } = useGET_meetingByIdQuery(id)
  const { data: protocol } = useGET_meetingProtocolByIdQuery(id)

  useErrorAlert(createError)
  useSuccessAlert(createSuccess ? `${t('alerts.entity-saved', {entity: 'Meeting protocol'})}` : '')

  const {
    location,
    notes,
    agenda,
    start_datetime,
    duration_minutes,
    participants,
  } = formModel()
  const disabled = entity?.status !== 'pending' && entity?.status !== 'active'


  let defaultValues: any = entity
    ? {
        ...entity, 
        facilitator: { id: '', title: '' },
        note_taker: { id: '', title: '' },
        notes: '',
        participants: entity?.participants.map((participant: IParticipant) => {
          return {
            participant: { id: participant.user_id, title: participant.title },
            role: participant.role,
            email: participant.email,
            phone: participant.phone,
            has_attended: participant.has_attended,
          }
        }),
      }
    : null

  if(protocol){
    defaultValues = {
      ...defaultValues,
      ...protocol,
    }
  }

  return (
    <Formik
      enableReinitialize
      initialValues={defaultValues}
      validationSchema={validationSchema}
      onSubmit={(values: any) => {
        const participantsIds = []
        values.participants.forEach((participant) => {
          if (participant.has_attended) {
            participantsIds.push(participant.participant.id)
          }
        })
        const data = {
          location: values.location,
          agenda: values.agenda,
          start_datetime: values.start_datetime,
          duration_minutes: values.duration_minutes,
          notes: values.notes,
          participants_attended_ids: participantsIds,
        }

        addEntity({ id, body: data })
      }}
    >
      {({ values, setFieldValue, errors }) => {
        return (
          <Form>
            <ScrollToError/>
            <Grid container spacing={4} sx={{ marginTop: '-1rem' }}>
              <Grid item xs={12} md={6}>
                <DateTimePickerField fieldProps={{ ...start_datetime }} disabled={viewOnly} />
              </Grid>
              <Grid item xs={12} md={6}>
                <InputField fieldProps={{ ...duration_minutes }} disabled={viewOnly} />
              </Grid>
              <Grid item xs={12}>
                <InputField fieldProps={{ ...location }} disabled={viewOnly} />
              </Grid>
              <Grid item xs={12}>
                <RichTextField fieldProps={{ ...agenda }} label={agenda.label} disabled={viewOnly} />
              </Grid>
              <Grid item xs={12}>
                <RichTextField fieldProps={{ ...notes }} label={notes.label} disabled={viewOnly} />
              </Grid>
              <Grid item xs={4}>
                <ParticipantList
                  fieldProps={{ ...participants }}
                  disabled={viewOnly || disabled}
                  isProtocol={true}
                />
              </Grid>
              <Grid item xs={4}>

                <div style={{ marginTop: '40px' }}>
                  <PageHeading title={t('meetings.has-confirmed')}/>
                </div>

                {
                    entity.participants.filter(p => p.has_confirmed).map(p => <div key={p.user_id} style={{ marginTop: '20px', display: 'flex' }}>
                      <div><CheckBoxOutlined /></div>
                      <div style={{ paddingLeft: '10px' }}>{p.title}</div>
                    </div>)
                }
                
              </Grid>
            </Grid>
            {/* ACTIONS */}
            <Grid container spacing={4} sx={{ marginTop: '1rem' }}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex' }}  className="transparent-background">
                  <Button
                    variant='outlined'
                    color='black'
                    onClick={() => navigate(backToSlug)}
                    sx={{ minHeight: '2.5rem', width: '10rem' }}
                  >
                    <FaChevronLeft style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                    {`${t('common.back')}`}
                  </Button>
                  {action !== 'view' && (
                    <SubmitButton
                      color={crudColorMap[action]}
                      text={`${t('form-actions.' + action)}`}
                      icon={crudIconMap[action]}
                      sx={{ width: '10rem' }}
                      isLoading={isLoading}
                    />
                  )}
                </Box>
              </Grid>
            </Grid>
          </Form>
        )
      }}
    </Formik>
  )
}

export default MeetingProtocolForm
